import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { AppDispatch, RootState } from '../../store/store';
import { createStudy, fetchStudies, Study, updateStudy } from '../../store/studySlice';

interface FormStudy extends Study {
  newStudy: boolean;
}

const validationSchema = Yup.object({
  description: Yup.string().required('Description is required'),
  inclusionCriteria: Yup.array().of(Yup.string().required("Le critère d'inclusion est requis")).required("Le critère d'inclusion est requis"),
  exclusionCriteria: Yup.array().of(Yup.string().required("Le critère d'exclusion est requis")).required("Le critère d'exclusion est requis"),
  usefulLinks: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required('le nom est requis'),
        url: Yup.string().url('URL invalide').required("L'URL est requise"),
      }),
    )
    .optional(),
});

const Studies: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { studies, loading, error } = useSelector((state: RootState) => state.studies);

  const [show, setShow] = useState(false);

  useEffect(() => {
    void dispatch(fetchStudies());
  }, [dispatch]);

  const formik = useFormik<FormStudy>({
    initialValues: {
      newStudy: true,
      id: '',
      description: '',
      inclusionCriteria: [''],
      exclusionCriteria: [''],
      usefulLinks: [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleSave(values);
    },
  });

  const handleClose = () => {
    setShow(false);
  };
  const handleShow = () => {
    setShow(true);
  };

  const handleSave = (values: FormStudy) => {
    // remove elements with empty string
    values.inclusionCriteria = values.inclusionCriteria.filter((value) => value !== '');
    values.exclusionCriteria = values.exclusionCriteria.filter((value) => value !== '');
    values.usefulLinks = values.usefulLinks?.filter((link) => link.name !== '' && link.url !== '');

    if (values.newStudy) {
      void dispatch(createStudy(values));
    } else {
      void dispatch(updateStudy(values));
    }
    handleClose();
  };

  const handleEdit = async (study: Study) => {
    await formik.setValues({ ...study, newStudy: false });
    handleShow();
  };

  const handleCreate = async () => {
    formik.resetForm();
    await formik.setValues({ ...formik.initialValues, newStudy: true });
    handleShow();
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 150 },
    { field: 'description', headerName: 'Description', width: 300 },
    {
      field: 'inclusionCriteria',
      headerName: "Critères d'inclusion",
      width: 300,
      renderCell: (params: GridRenderCellParams<Study, string[]>) => params.value?.join(', '),
    },
    {
      field: 'exclusionCriteria',
      headerName: "Critères d'exclusion",
      width: 300,
      renderCell: (params: GridRenderCellParams<Study, string[]>) => params.value?.join(', '),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: (params: GridRenderCellParams<Study>) => (
        <Box display="flex" justifyContent="space-between" sx={{ p: 1 }} width="100%">
          <Button
            color="primary"
            onClick={() => {
              void handleEdit(params.row);
            }}
            variant="contained"
          >
            Edit
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Container>
      <Typography gutterBottom variant="h4">
        Etudes
      </Typography>
      {loading && <CircularProgress />}
      <Button
        color="primary"
        onClick={() => {
          void handleCreate();
        }}
        variant="contained"
      >
        Nouvelle étude
      </Button>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 4 }}>
        <DataGrid columns={columns} rowCount={studies.length} rows={studies} />
      </Box>

      <Dialog fullWidth maxWidth="md" onClose={handleClose} open={show}>
        <DialogTitle>{formik.values.newStudy ? 'Créer une étude' : 'Editer une étude'}</DialogTitle>
        <DialogContent>
          <form onSubmit={formik.handleSubmit}>
            {formik.values.newStudy ? (
              <TextField
                error={formik.touched.id && Boolean(formik.errors.id)}
                fullWidth
                helperText={formik.touched.id && formik.errors.id}
                id="id"
                label="Code de l'étude"
                margin="dense"
                name="id"
                onChange={formik.handleChange}
                type="text"
                value={formik.values.id}
              />
            ) : (
              <TextField
                fullWidth
                id="id"
                label="Code de l'étude"
                margin="dense"
                name="id"
                slotProps={{ input: { readOnly: true, disabled: true } }}
                type="text"
                value={formik.values.id}
              />
            )}
            <TextField
              error={formik.touched.description && Boolean(formik.errors.description)}
              fullWidth
              helperText={formik.touched.description && formik.errors.description}
              id="description"
              label="Description"
              margin="dense"
              name="description"
              onChange={formik.handleChange}
              type="text"
              value={formik.values.description}
            />

            <Box>
              <Typography sx={{ mt: 2 }} variant="h6">
                {"Critères d'inclusion"}
              </Typography>
              <IconButton
                onClick={() => {
                  const newInclusionCriteria = [...formik.values.inclusionCriteria, ''];
                  void formik.setFieldValue('inclusionCriteria', newInclusionCriteria);
                }}
              >
                <AddIcon />
              </IconButton>
              {formik.values.inclusionCriteria.map((_, index) => (
                <Box alignItems="center" display="flex" key={index}>
                  <TextField
                    error={formik.touched.inclusionCriteria && Boolean(formik.errors.inclusionCriteria?.[index])}
                    fullWidth
                    helperText={formik.touched.inclusionCriteria && formik.errors.inclusionCriteria?.[index]}
                    id={`formik.values.inclusionCriteria.${index.toString()}`}
                    label={`Critère d'inclusion ${(index + 1).toString()}`}
                    margin="dense"
                    name={`formik.values.inclusionCriteria.${index.toString()}`}
                    onChange={(value) => {
                      const newInclusionCriteria = [...formik.values.inclusionCriteria];
                      newInclusionCriteria[index] = value.target.value;
                      void formik.setFieldValue('inclusionCriteria', newInclusionCriteria);
                    }}
                    type="text"
                    value={formik.values.inclusionCriteria[index]}
                  />
                  <IconButton
                    onClick={() => {
                      const newInclusionCriteria = [...formik.values.inclusionCriteria];
                      newInclusionCriteria.splice(index, 1);
                      void formik.setFieldValue('inclusionCriteria', newInclusionCriteria);
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Box>
              <Typography sx={{ mt: 2 }} variant="h6">
                {"Critères d'exclusion"}
              </Typography>
              <IconButton
                onClick={() => {
                  const newExclusionCriteria = [...formik.values.exclusionCriteria, ''];
                  void formik.setFieldValue('exclusionCriteria', newExclusionCriteria);
                }}
              >
                <AddIcon />
              </IconButton>
              {formik.values.exclusionCriteria.map((_, index) => (
                <Box alignItems="center" display="flex" key={index}>
                  <TextField
                    error={formik.touched.exclusionCriteria && Boolean(formik.errors.exclusionCriteria?.[index])}
                    fullWidth
                    helperText={formik.touched.exclusionCriteria && formik.errors.exclusionCriteria?.[index]}
                    id={`exclusionCriteria.${index.toString()}`}
                    label={`Critère d'exclusion ${(index + 1).toString()}`}
                    margin="dense"
                    name={`exclusionCriteria.${index.toString()}`}
                    onChange={(value) => {
                      const newExclusionCriteria = [...formik.values.exclusionCriteria];
                      newExclusionCriteria[index] = value.target.value;
                      void formik.setFieldValue('exclusionCriteria', newExclusionCriteria);
                    }}
                    type="text"
                    value={formik.values.exclusionCriteria[index]}
                  />
                  <IconButton
                    onClick={() => {
                      const newExclusionCriteria = [...formik.values.exclusionCriteria];
                      newExclusionCriteria.splice(index, 1);
                      void formik.setFieldValue('exclusionCriteria', newExclusionCriteria);
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Box>
              <Typography sx={{ mt: 2 }} variant="h6">
                Liens utiles
              </Typography>
              <IconButton
                onClick={() => {
                  const newUsefulLinks = formik.values.usefulLinks ? [...formik.values.usefulLinks, { name: '', url: '' }] : [{ name: '', url: '' }];
                  void formik.setFieldValue('usefulLinks', newUsefulLinks);
                }}
              >
                <AddIcon />
              </IconButton>
              {formik.values.usefulLinks?.map((_, index) => {
                const touched = (formik.touched.usefulLinks as unknown as { name: boolean; url: boolean }[] | undefined)?.[index];
                const errors = (formik.errors.usefulLinks as unknown as { name: string; url: string }[] | undefined)?.[index];

                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mt: 2, gap: 2 }}>
                    <TextField
                      error={touched?.name && Boolean(errors?.name)}
                      fullWidth
                      helperText={touched?.name && errors?.name}
                      id={`usefulLinks[${index.toString()}].name`}
                      label="Nom"
                      margin="dense"
                      name={`usefulLinks[${index.toString()}].name`}
                      onChange={formik.handleChange}
                      type="text"
                      value={formik.values.usefulLinks?.[index].name}
                    />
                    <TextField
                      error={touched?.url && Boolean(errors?.url)}
                      fullWidth
                      helperText={touched?.url && errors?.url}
                      id={`usefulLinks[${index.toString()}].url`}
                      label="URL"
                      margin="dense"
                      name={`usefulLinks[${index.toString()}].url`}
                      onChange={formik.handleChange}
                      type="text"
                      value={formik.values.usefulLinks?.[index].url}
                    />

                    <IconButton
                      onClick={() => {
                        const newUsefulLinks = formik.values.usefulLinks ? [...formik.values.usefulLinks] : [];
                        newUsefulLinks.splice(index, 1);
                        void formik.setFieldValue('usefulLinks', newUsefulLinks);
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>

            <DialogActions>
              <Button color="primary" onClick={handleClose}>
                Annuler
              </Button>
              <Button color="primary" type="submit">
                {formik.values.newStudy ? 'Créer' : 'Mettre à jour'}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Studies;
